/**
 * Extrai uma mensagem de erro legível de uma resposta de erro Axios, cobrindo os formatos
 * realmente usados pelos back-ends: `erros`/`Erros` (array do ErrorModel, Kernel e Academic API),
 * `mensagem`/`Message` (string única) e o `ValidationProblemDetails` automático do ASP.NET Core
 * (`{ title, errors: { campo: [msgs] } }`, devolvido quando a validação de model do controller falha).
 * Retorna string vazia quando nenhum formato reconhecido é encontrado.
 */
export function extrairMensagemErroHttp(err: any): string {
  const data = err?.response?.data;

  if (!data) return '';

  if (typeof data === 'string' && data.trim())
    return data;

  /*
   * O CORPO PODE SER O PRÓPRIO ARRAY.
   *
   * Vários endpoints devolvem a lista de erros na raiz — `["Nenhum estudante pôde ser
   * rematriculado. FULANO: Já possui matrícula em 2026."]` — em vez de embrulhá-la em
   * `{ erros: [...] }`. Sem este ramo a função caía no `return ''` final e quem chamou exibia o
   * texto genérico de fallback (ou, pior, um aviso VAZIO), escondendo justamente a única
   * informação que dizia ao usuário o que fazer.
   */
  if (Array.isArray(data))
    return itensDeErro(data).join(' ');

  if (typeof data.Message === 'string' && data.Message.trim())
    return data.Message;

  if (typeof data.mensagem === 'string' && data.mensagem.trim())
    return data.mensagem;

  if (Array.isArray(data.erros) && data.erros.length)
    return itensDeErro(data.erros).join(' ');

  if (Array.isArray(data.Erros) && data.Erros.length)
    return itensDeErro(data.Erros).join(' ');

  if (data.errors && typeof data.errors === 'object') {
    const mensagensPorCampo = Object.entries(data.errors as Record<string, string[]>)
      .filter(([, mensagens]) => Array.isArray(mensagens) && mensagens.length > 0)
      .map(([campo, mensagens]) => `${campo}: ${mensagens.join(', ')}`);

    if (mensagensPorCampo.length)
      return mensagensPorCampo.join(' | ');
  }

  if (typeof data.title === 'string' && data.title.trim())
    return data.title;

  /*
   * ÚLTIMA TENTATIVA pela lista normalizada — hoje é o que cobre as notificações do Flunt
   * (`{ notifications: [{ message }] }`), que esta função não lia e que passavam batido: o
   * interceptor global exibia o texto genérico de fallback em vez do motivo real da recusa.
   *
   * Fica por último de propósito: os ramos acima formatam melhor o caso que reconhecem (o
   * `errors` do ASP.NET Core sai com o NOME DO CAMPO na frente, que a lista descarta).
   */
  const lista = normalizarListaErros(data);

  return lista.length ? lista.join(' ') : '';
}

/**
 * Mesma leitura, mas sobre o CORPO já desembrulhado da resposta (o que os services devolvem em vez
 * de propagar o erro do Axios) e devolvendo a lista, para o modal que mostra um erro por linha.
 *
 * Cobre o `ErrorModel` das duas APIs (`erros`/`Erros`), as notificações do Flunt (`notifications`,
 * quando alguém devolve o Result cru) e o `ValidationProblemDetails` do ASP.NET Core — este último
 * é o que aparece quando o JSON enviado não casa com o contrato do endpoint, e sem tratá-lo a tela
 * mostra apenas "Erro desconhecido".
 */
export function normalizarListaErros(data: any): string[] {
  if (!data) return [];

  if (typeof data === 'string') return data.trim() ? [data] : [];

  if (Array.isArray(data)) return itensDeErro(data);

  if (Array.isArray(data.erros) && data.erros.length) return itensDeErro(data.erros);
  if (Array.isArray(data.Erros) && data.Erros.length) return itensDeErro(data.Erros);

  const notificacoes = data.notifications ?? data.Notifications;

  if (Array.isArray(notificacoes) && notificacoes.length) return itensDeErro(notificacoes);

  if (data.errors && typeof data.errors === 'object') {
    const mensagens = Object.values(data.errors as Record<string, string[]>)
      .flatMap((m) => (Array.isArray(m) ? m : [m]))
      .map(String)
      .filter((m) => m.trim().length > 0);

    if (mensagens.length) return mensagens;
  }

  for (const campo of ['mensagem', 'Message', 'title']) {
    const valor = data[campo];
    if (typeof valor === 'string' && valor.trim()) return [valor];
  }

  return [];
}

/**
 * Os itens de uma lista de erros, venham eles como STRING ou como objeto de notificação.
 *
 * <p>É a mesma lista em dois formatos: o `ErrorModel` das duas APIs manda `["mensagem", ...]` e o
 * Flunt manda `[{ message: "mensagem" }, ...]`. Ler só um dos dois é o que produzia o aviso
 * VAZIO: aplicar `n.message` sobre um array de strings devolve `undefined` em toda posição, o
 * `?? ''` transforma isso em texto em branco, e o SweetAlert2 com `title: ''` desenha o balão só
 * com o ícone — foi exatamente o que apareceu na rematrícula.</p>
 */
function itensDeErro(lista: any[]): string[] {
  return lista
    .map((item: any) => {
      if (typeof item === 'string') return item.trim();

      if (item && typeof item === 'object')
        return String(item.message ?? item.Message ?? item.mensagem ?? '').trim();

      return item == null ? '' : String(item).trim();
    })
    .filter((m: string) => m.length > 0);
}
